import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';

import { AccountInvitationState } from 'src/app/models/AccountMember';
import { AccountMemberService } from 'src/app/shared/services/account-member.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ToastService } from 'src/app/shared/components/toast/toast.service';

/**
 * Estado da tela. `loading` até a primeira resposta; `done` é o aceite que
 * acabou de acontecer aqui, e `accepted` o convite que já tinha sido aceito
 * antes (link aberto de novo). `error` é a consulta que não teve resposta
 * (rede, 429 do throttle, 5xx) — o backend responde 200 com o `status` para
 * qualquer token, então falha HTTP não diz nada sobre o convite.
 */
export type InviteAcceptView = 'loading' | 'valid' | 'expired' | 'invalid' | 'accepted' | 'done' | 'error';

const PASSWORD_MIN_LENGTH = 8;

/**
 * Aceite do convite de membro (Multiusuários v1.0, CU-17tm466h1cm — tela 6).
 *
 * Página pública, fora do `LayoutComponent`, no mesmo molde do `password-reset`:
 * o membro define a senha e passa a entrar com o próprio e-mail. As regras de
 * senha são as do backend (mínimo de 8 e confirmação) — o checklist mostra só o
 * que o servidor de fato exige.
 *
 * O e-mail não é editável: o convite foi feito para ele, e trocar exigiria um
 * novo convite do dono.
 */
@Component({
  selector: 'app-invite-accept',
  templateUrl: './invite-accept.component.html',
  styleUrls: ['./invite-accept.component.scss']
})
export class InviteAcceptComponent implements OnInit, OnDestroy {

  readonly minLength = PASSWORD_MIN_LENGTH;

  view: InviteAcceptView = 'loading';
  invitation: AccountInvitationState = null;
  isSubmitting = false;

  form: UntypedFormGroup;

  private token: string;
  private loads = new Subject<string>();
  private subscriptions = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private toast: ToastService,
    private accountMemberService: AccountMemberService
  ) { }

  /**
   * Nome de quem já está logado neste navegador. O login manda sessão aberta
   * direto para o painel, então quem aceita o convite no computador do dono
   * entraria como o dono sem perceber.
   */
  get loggedAs(): string | null {
    if (!this.authService.isLogged()) {
      return null;
    }

    const user = this.authService.getUser(true);

    return this.authService.currentMember?.email ?? user?.email ?? null;
  }

  get password(): string {
    return this.form.get('password').value || '';
  }

  get hasMinLength(): boolean {
    return this.password.length >= PASSWORD_MIN_LENGTH;
  }

  get passwordsMatch(): boolean {
    return this.password !== '' && this.password === this.form.get('password_confirmation').value;
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)]],
      password_confirmation: ['', Validators.required],
    }, { validators: matchingPasswords });

    // switchMap: trocar o token na URL descarta a consulta anterior.
    this.subscriptions.add(
      this.loads.pipe(
        switchMap(token => {
          this.token = token;
          this.view = 'loading';

          // O erro fica dentro: deixar escapar encerraria o `loads` e o
          // "Tentar de novo" não faria mais nada.
          return this.accountMemberService.invitation(token).pipe(
            catchError(() => {
              this.view = 'error';
              return EMPTY;
            })
          );
        })
      ).subscribe(state => {
        this.invitation = state;
        this.view = state.status;
      })
    );

    this.subscriptions.add(
      this.route.params.subscribe(params => this.loads.next(params.token))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  accept(): void {
    if (this.form.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    this.subscriptions.add(
      this.accountMemberService
        .acceptInvitation(this.token, this.password)
        .pipe(finalize(() => this.isSubmitting = false))
        .subscribe({
          next: () => this.view = 'done',
          // 410: o convite expirou ou foi cancelado enquanto a tela estava
          // aberta. O 422 de senha e a falta de rede (status 0) já viram
          // toast no interceptor; o resto (429 do throttle, 5xx) não.
          error: (error: HttpErrorResponse) => {
            if (error.status === 410) {
              this.view = 'expired';
            } else if (error.status === 429) {
              this.toast.error('Muitas tentativas. Aguarde um minuto e tente novamente.');
            } else if (error.status !== 422 && error.status !== 0) {
              this.toast.error('Não foi possível criar o acesso agora. Tente novamente.');
            }
          }
        })
    );
  }

  /** Recarrega a consulta depois de uma falha sem resposta. */
  retry(): void {
    this.loads.next(this.token);
  }

  /** Encerra a sessão aberta (se houver) antes de ir para o login. */
  goToLogin(): void {
    if (this.authService.isLogged()) {
      this.authService.logout();
    }

    this.router.navigate(['/login']);
  }
}

function matchingPasswords(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password').value;
  const confirmation = group.get('password_confirmation').value;

  return password && confirmation && password !== confirmation ? { different: true } : null;
}
